import { Inject, Injectable, Optional } from '@angular/core';
import { AWTEventProperties, AWTLogConfiguration, AWTLogger, AWTLogManager, AWTPiiKind, AWTSessionState, AWTUserIdType } from '@aria/webjs-sdk';

import { WrapperVersion } from '../models/wrapper-version.model';
import { Performance } from '../services/performance.service';
import { UnloadEventService } from '../services/unload-event.service';
import { WRAPPER_VERSION } from '../services/wrapper-version.token';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfig, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { UserContextService } from '../shared-context/user-context.service';
import { ActivitySessionService } from './activity-session.service';
import { DispatcherEventFactoryService } from './dispatcher-instrumentation.service';
import { InstrumentationEvent, InstrumentationParam, InstrumentationParams, PiiString } from './instrumentation.service';
import { UserProfileService } from './user-profile.service';

/**
 * Sends client logs to Aria using their Javascript SDK.
 * For developer docs on Aria API and how to use it. See https://aria.microsoft.com/developer/
 */
@Injectable({
    providedIn: 'root'
})
export class AriaService {
    private static readonly unknownUserId = 'Unknown';
    private static readonly legacyAriaCookies = ['MicrosoftApplicationsTelemetryDeviceId', 'MicrosoftApplicationsTelemetryFirstLaunchTime'];
    private static readonly removeCookieString = '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    private readonly ariaLogger: AWTLogger;
    private static readonly propertyMap = new Map<string, string>();
    private sessionActive: boolean = false;

    constructor(
        capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        userContextService: UserContextService,
        private readonly activitySessionService: ActivitySessionService,
        userProfileService: UserProfileService,
        unloadEventService: UnloadEventService,
        @Optional() @Inject(WRAPPER_VERSION) wrapperVersion: WrapperVersion
    ) {
        this.ariaLogger = AriaService.createAriaLogger(clientStartupConfigService.config, userContextService, userProfileService, wrapperVersion, capabilitiesService);

        if (this.ariaLogger) {
            this.sessionActive = true;
            // Manage page view session
            unloadEventService.addUnloadEvent(() => {
                this.endAppSession(!capabilitiesService.capabilities.pwaEnabled);
            }, true);
        }
    }

    public static createAriaLogger(
        startupConfig: ClientStartupConfig,
        userContextService?: UserContextService,
        userProfileService?: UserProfileService,
        wrapperVersion?: WrapperVersion,
        capabilitiesService?: CapabilitiesService
    ): AWTLogger {
        if (!(startupConfig.sharedClientStartupContext.ariaTenant && startupConfig.sharedClientStartupContext.ariaCollectorUrl)) {
            return null;
        }

        const ariaLogger = AriaService.initializeAriaLogManager(
            startupConfig.sharedClientStartupContext.ariaTenant,
            startupConfig.sharedClientStartupContext.ariaCollectorUrl);

        let puid = startupConfig.puid;
        let idType = AWTUserIdType.Unknown;
        if (puid) {
            if (startupConfig.authType === AuthType.OrgId) {
                idType = AWTUserIdType.OrgIdPUID;
            } else if (startupConfig.authType === AuthType.MSA) {
                idType = AWTUserIdType.MSAPUID;
            }
        } else {
            puid = AriaService.unknownUserId;
        }

        // Based on latest guidance from Michael Stokes (Privacy lead for OXO) it is okay to log PUID for both MSA and OrgId users
        ariaLogger.getSemanticContext().setUserId(puid, AWTPiiKind.NotSet, idType);
        ariaLogger.setContext('UserInfo.OMSTenantId', startupConfig.tenantId);

        ariaLogger.setContext('AuthType', startupConfig.authTypeString);
        ariaLogger.setContext('AuthVer', startupConfig.authVersion);
        ariaLogger.setContext('CorpNet', startupConfig.sharedClientStartupContext.corpNet);
        ariaLogger.setContext('CorrelationId', startupConfig.sharedClientStartupContext.correlationId);
        ariaLogger.setContext('DevEnvironment', startupConfig.sharedClientStartupContext.devEnvironment);
        ariaLogger.setContext('DeploymentEnvironment', startupConfig.sharedClientStartupContext.deploymentEnvironment);
        ariaLogger.setContext('Geo', startupConfig.sharedClientStartupContext.geoName);
        ariaLogger.setContext('RequestOrigin', startupConfig.sharedClientStartupContext.requestOrigin);
        ariaLogger.setContext('SessionId', startupConfig.sharedClientStartupContext.sessionId);
        ariaLogger.setContext('TestTraffic', startupConfig.sharedClientStartupContext.testTraffic);
        ariaLogger.setContext('Workload', capabilitiesService.getWorkload());
        ariaLogger.setContext(InstrumentationParam.ServerBuildDate, startupConfig.sharedClientStartupContext.buildDateUtc);
        ariaLogger.setContext(InstrumentationParam.ServerBuildId, startupConfig.sharedClientStartupContext.buildId);
        ariaLogger.setContext(InstrumentationParam.BundleBuildDate, process.env.BUILD_DATE);
        ariaLogger.setContext(InstrumentationParam.BundleBuildId, process.env.BUILD_ID);
        ariaLogger.setContext('EcsRing', startupConfig.sharedClientStartupContext.ecsRing);
        ariaLogger.setContext('DomainOrigin', startupConfig.sharedClientStartupContext.domainOrigin);

        // Log flights and features together
        const flightsAndFeaturesSeparator = startupConfig.sharedClientStartupContext.flights && startupConfig.features ? ',' : '';
        const flightsAndFeaturesConcatenated = startupConfig.sharedClientStartupContext.flights + flightsAndFeaturesSeparator + startupConfig.features;
        ariaLogger.setContext('Flights', flightsAndFeaturesConcatenated);

        if (userContextService) {
            ariaLogger.setContext('Admin', userContextService.user.isAdmin);
            ariaLogger.setContext('IsSmb', userContextService.user.isSmb);
            ariaLogger.setContext('IsIw', userContextService.user.isIw);
            ariaLogger.setContext('IsEdu', userContextService.user.isEdu);
            ariaLogger.setContext('IsEduStudent', userContextService.user.isEduStudent);
            ariaLogger.setContext('IsEduFaculty', userContextService.user.isEduFaculty);
            ariaLogger.setContext('IsDynamics', userContextService.user.isDynamics);
            ariaLogger.setContext('ThemeId', userContextService.user.themeId);
            ariaLogger.setContext('IsFirstRelease', userContextService.user.isFirstRelease);
        }

        if (userProfileService) {
            ariaLogger.setContext('FirstVisit', userProfileService.isFirstVisit());
            ariaLogger.setContext('FirstVisitAgeInDays', userProfileService.fullDaysSinceFirstVisit());
        }

        if (wrapperVersion) {
            ariaLogger.setContext('WrapperVersion', `${wrapperVersion.major}.${wrapperVersion.minor}.${wrapperVersion.build}.${wrapperVersion.revision}`);

            //Add PWA app workload type
            let appWorkloadType: string;
            if (capabilitiesService.capabilities.isPwaInWebView2) {
                appWorkloadType = 'WebView2';
            } else if (capabilitiesService.capabilities.isPwaInUwp) {
                const webView2Version = new RegExp('1[0-9]{2}10');
                if (wrapperVersion?.build && webView2Version.test((wrapperVersion.build).toString())) {
                    // UWP appType launched as a fallback due to WebView2 runtime not installed on end user system
                    appWorkloadType = 'UWP_fallback';
                } else {
                    appWorkloadType = 'UWP';
                }
            }
            if (appWorkloadType) {
                ariaLogger.setContext('AppWorkloadType', appWorkloadType);
            }
        }

        AriaService.startSession(ariaLogger);

        return ariaLogger;
    }

    public static endSession(ariaLogger: AWTLogger, teardown: boolean, sessionProperties?: AWTEventProperties) {
        if (!window.disableLogs) {
            sessionProperties = sessionProperties || new AWTEventProperties();
            AriaService.populateSessionProperties(sessionProperties);
            ariaLogger.logSession(AWTSessionState.Ended, sessionProperties);
        }

        if (teardown) {
            AWTLogManager.flushAndTeardown();
        } else {
            AWTLogManager.flush(null);
        }
    }

    /**
     * Tracks user event with key/value params
     */
    public trackEvent(
        streamName: string,
        eventParams: { [index: string]: any }) {
        if (!this.ariaLogger) {
            return;
        }

        if (!this.sessionActive) {
            AriaService.startSession(this.ariaLogger);
            this.sessionActive = true;
        }

        const startupConfig = this.clientStartupConfigService.config;
        const ariaProps = new AWTEventProperties();
        ariaProps.setName(streamName);
        for (const name in eventParams) {
            if (eventParams.hasOwnProperty(name)) {
                const param = eventParams[name];
                if (!(param instanceof PiiString)) {
                    ariaProps.setProperty(name, param);
                } else if (startupConfig.logPiiAllowed) {
                    if (startupConfig.logPiiInClearText) {
                        ariaProps.setProperty(name, param.value);
                    } else {
                        ariaProps.setPropertyWithPii(name, param.value, param.kind);
                    }
                }
            }
        }
        this.ariaLogger.logEvent(ariaProps);
    }

    public endAppSession(teardown: boolean) {
        if (!this.ariaLogger || !this.sessionActive) {
            return;
        }

        // Record session activity stats
        const sessionProperties = new AWTEventProperties();
        const activityStats = this.activitySessionService.endSession();
        if (activityStats) {
            // Record activity/inactivity duration in seconds to be consistent with how Aria records page session duration
            const inactivitySinceLastActivitySession = Performance.now() - activityStats.inactivityStartTime;
            sessionProperties.setProperty(InstrumentationParam.ActivityDuration, Math.ceil(activityStats.cumulativeDurationOfActivitySessions / 1000).toString());
            sessionProperties.setProperty(InstrumentationParam.InactivityDuration, Math.ceil((activityStats.cumulativeDurationBetweenActivitySessions + inactivitySinceLastActivitySession) / 1000).toString());
            sessionProperties.setProperty(InstrumentationParam.ItemCount, activityStats.activitySessionsCount.toString());
            sessionProperties.setProperty(InstrumentationParam.ClickCount, activityStats.cumulativeClickCount.toString());
        }
        AriaService.endSession(this.ariaLogger, teardown, sessionProperties);
        this.sessionActive = false;
    }

    private static startSession(ariaLogger: AWTLogger) {
        if (window.disableLogs) {
            return;
        }

        const sessionProps = new AWTEventProperties();
        AriaService.populateSessionProperties(sessionProps);
        ariaLogger.logSession(AWTSessionState.Started, sessionProps);
    }

    private static populateSessionProperties(sessionProps: AWTEventProperties) {
        sessionProps.setProperty(InstrumentationParam.LoggerId, 'Angular');

        // Applicable only to PWA where app is prelaunched but user hasn't interacted with it
        if (window.prelaunchMode) {
            sessionProps.setProperty('PrelaunchMode', 'true');
        }
    }

    private static initializeAriaLogManager(ariaTenant: string, ariaCollectorUrl: string): AWTLogger {
        // IMPORTANT: enableAutoUserSession should be set to false because we want to log page view session ourselves
        // to ensure that all context properties are logged with Aria session event
        const officePropertyStorageOverride: AWTPropertyStorageOverride = {
            setProperty: (key: string, value: string) => AriaService.setProperty(key, value),
            getProperty: (key: string) => AriaService.getProperty(key)
        };

        const logConfig: AWTLogConfiguration = {
            collectorUri: ariaCollectorUrl,
            enableAutoUserSession: false,
            propertyStorageOverride: officePropertyStorageOverride,
            disableCookiesUsage: true
        };

        // Remove existing aria cookies on auth page
        if (document && document.cookie) {
            AriaService.legacyAriaCookies.forEach(cookie => {
                document.cookie = cookie + AriaService.removeCookieString;
            });
        }

        return AWTLogManager.initialize(ariaTenant, logConfig);
    }

    private static setProperty(key: string, value: string) {
        AriaService.propertyMap.set(key, value);
    }

    private static getProperty(key: string): string {
        return AriaService.propertyMap.has(key) ? this.propertyMap.get(key) : '';
    }
}

class AriaInstrumentationEvent implements InstrumentationEvent {

    constructor(
        private readonly ariaService: AriaService,
        private readonly streamName: string,
        public params: InstrumentationParams,
        public metrics: Array<string>
    ) {
    }

    public record(): void {
        const eventParams: any = {};
        for (const param in this.params) {
            const key = param;
            const value = this.params[param];
            eventParams[key] = value;
        }

        this.ariaService.trackEvent(this.streamName, eventParams);
    }
}

/**
 * Factory that is responsible for creating Aria instrumentation instances
 */
@Injectable({
    providedIn: 'root'
})
export class AriaEventFactoryService implements DispatcherEventFactoryService {

    constructor(
        private readonly ariaService: AriaService
    ) {
    }

    public createEvent(
        streamName: string,
        params: InstrumentationParams,
        metrics: Array<string>): InstrumentationEvent {

        return new AriaInstrumentationEvent(this.ariaService, streamName, params, metrics);
    }

    public flush() {
        this.ariaService.endAppSession(true);
    }
}
